import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Bell,
  Mail,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Trash2,
  Settings,
  Filter,
  Check,
  TrendingUp,
  Users,
  Target,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

interface Notification {
  id: string;
  type: "deal" | "contact" | "activity" | "alert" | "system";
  title: string;
  message: string;
  severity?: "info" | "warning" | "critical";
  isRead: boolean;
  createdAt: string;
  actionUrl?: string;
  metadata?: {
    dealId?: string;
    contactId?: string;
    companyId?: string;
  };
}

interface NotificationsCenterProps {
  className?: string;
}

const typeIcons: Record<string, React.ReactNode> = {
  deal: <Target className="h-4 w-4" />,
  contact: <Users className="h-4 w-4" />,
  activity: <Zap className="h-4 w-4" />,
  alert: <AlertTriangle className="h-4 w-4" />,
  system: <Bell className="h-4 w-4" />,
};

const typeColors: Record<string, string> = {
  deal: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  contact: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  activity: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  alert: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  system: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400",
};

export function NotificationsCenter({ className }: NotificationsCenterProps) {
  const [activeTab, setActiveTab] = useState("all");
  const [showRead, setShowRead] = useState(true);
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["/api/notifications"],
    queryFn: async (): Promise<Notification[]> => {
      const res = await apiRequest("GET", "/api/notifications");
      if (!res.ok) throw new Error("Failed to fetch notifications");
      return res.json();
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const displayNotifications = notifications;

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("PATCH", `/api/notifications/${id}/read`);
      if (!res.ok) throw new Error("Failed to mark as read");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", "/api/notifications/read-all");
      if (!res.ok) throw new Error("Failed to mark all as read");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast.success("All notifications marked as read");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/notifications/${id}`);
      if (!res.ok) throw new Error("Failed to delete notification");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  const filteredNotifications = displayNotifications.filter((n) => {
    if (activeTab !== "all" && n.type !== activeTab) return false;
    if (!showRead && n.isRead) return false;
    return true;
  });

  const unreadCount = displayNotifications.filter((n) => !n.isRead).length;
  const criticalCount = displayNotifications.filter((n) => n.severity === "critical" && !n.isRead).length;

  return (
    <Card className={cn("w-full max-w-md", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notifications
            </CardTitle>
            {unreadCount > 0 && (
              <Badge variant="default" className="text-xs">
                {unreadCount} new
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllReadMutation.mutate()}
                className="h-8 text-xs"
              >
                <Check className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 h-8">
            <TabsTrigger value="all" className="text-xs">
              All
              {unreadCount > 0 && (
                <span className="ml-1 text-xs bg-primary text-primary-foreground rounded-full px-1.5">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="deal" className="text-xs">Deals</TabsTrigger>
            <TabsTrigger value="alert" className="text-xs">
              Alerts
              {criticalCount > 0 && (
                <span className="ml-1 text-xs bg-red-500 text-white rounded-full px-1.5">
                  {criticalCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="activity" className="text-xs">Activity</TabsTrigger>
            <TabsTrigger value="system" className="text-xs">System</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <Filter className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Show read</span>
            <Switch checked={showRead} onCheckedChange={setShowRead} className="scale-75" />
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <ScrollArea className="h-[400px]">
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No notifications</p>
              <p className="text-sm">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "flex items-start gap-3 p-4 transition-colors hover:bg-accent/50 cursor-pointer",
                    !notification.isRead && "bg-blue-50/50 dark:bg-blue-950/40"
                  )}
                  onClick={() => {
                    if (!notification.isRead) {
                      markReadMutation.mutate(notification.id);
                    }
                    if (notification.actionUrl) {
                      window.location.href = notification.actionUrl;
                    }
                  }}
                >
                  <div
                    className={cn(
                      "p-2 rounded-lg mt-0.5",
                      typeColors[notification.type]
                    )}
                  >
                    {typeIcons[notification.type]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={cn("font-medium text-sm", !notification.isRead && "text-foreground")}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {notification.message}
                        </p>
                      </div>
                      {!notification.isRead && (
                        <div className="w-2 h-2 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.createdAt))} ago
                      </span>
                      {notification.severity === "critical" && (
                        <Badge variant="destructive" className="text-[10px] h-4">
                          Critical
                        </Badge>
                      )}
                      {notification.severity === "warning" && (
                        <Badge variant="secondary" className="text-[10px] h-4">
                          Warning
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(notification.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

// Standalone notification bell for header
export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const { data: notifications = [] } = useQuery({
    queryKey: ["/api/notifications/unread-count"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/notifications/unread-count");
      if (!res.ok) return { count: 0 };
      return res.json();
    },
    refetchInterval: 30000,
  });

  const unreadCount = notifications.count || 3; // Mock

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 z-50">
          <NotificationsCenter />
        </div>
      )}
    </div>
  );
}
